import { Tooltip } from "@mui/material";
import * as React from "react";

/**
 * Reveals the full text of anything the CSS has clipped with an ellipsis —
 * table cells, autocomplete inputs and options, chips — from a single listener
 * mounted once at the app root.
 *
 * The obvious implementation, a ResizeObserver per element, is what this
 * replaces. Observing an element that lives inside a component which itself
 * measures on render (MUI's Autocomplete) sets up a feedback loop: observer
 * fires, state changes, the parent re-lays-out, the observer fires again. On a
 * filter drawer with a dozen dropdowns that pinned the CPU.
 *
 * So: measure nothing until the pointer actually rests somewhere, then measure
 * once. Nothing is observed, and no component re-renders except this one.
 */
/**
 * How long to hold a closed tooltip's anchor before releasing it. Comfortably
 * longer than MUI's ~195ms exit transition, so the fade-out still has somewhere
 * to sit, but short enough that no detached node is retained for long.
 */
const ANCHOR_RELEASE_MS = 400;

/**
 * Components that legitimately own a tooltip over ellipsised content mark
 * themselves — and their subtree — with this, so the two cannot double up.
 * The association date field is the case that needs it: its ellipsis sits on
 * `.MuiOutlinedInput-input`, but its tooltip has to cover the whole field
 * including padding, border and the calendar adornment, and browsers suppress
 * mouse events from a disabled input entirely. Removing that component's own
 * tooltip and restoring it were both wrong; this is the mechanism that lets
 * one side own the behaviour outright.
 */
const OPT_OUT = '[data-overflow-tooltip="off"]';

/**
 * A field whose value is deliberately hidden must never be echoed back. The app
 * theme puts `text-overflow: ellipsis` on every MuiInputBase input, password
 * fields included, so without this a long passphrase clips and then renders in
 * full, in cleartext, above the dots that are masking it.
 */
const isMaskedInput = (el: HTMLElement) =>
  el instanceof HTMLInputElement && el.type === "password";

/**
 * An <input> holds its value in .value; everything else in .innerText.
 * innerText, not textContent: a cell holding two stacked lines would otherwise
 * read back as "Display: 1,000,000 (Imp)Linkedin: 500 (USD)".
 */
const readText = (el: HTMLElement) =>
  el instanceof HTMLInputElement
    ? el.value.trim()
    : (el.innerText ?? el.textContent ?? "").trim();

/**
 * Layout-free identity of an element's text, for change detection only.
 * `textContent` and `input.value` never force reflow; `innerText` does. This
 * runs on every poll tick while a tooltip is open, so it must be the cheap one
 * — comparing `readText` here reflowed the page 2.5 times a second, in the one
 * component whose whole purpose is to avoid exactly that.
 */
const signatureOf = (el: HTMLElement) =>
  el instanceof HTMLInputElement ? el.value : el.textContent ?? "";

const isClipped = (el: HTMLElement) =>
  el.scrollWidth > el.clientWidth || el.scrollHeight > el.clientHeight;

export default function OverflowTooltip() {
  // `open` is tracked separately from `anchorEl` on purpose. Clearing the anchor
  // at the moment of closing left the Popper without one for the length of its
  // exit transition, so it fell back to the placeholder child below — which sits
  // at the document origin — and the tooltip flashed in the top-left corner on
  // the way out. Keeping the last anchor lets it fade out where it was shown.
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const [open, setOpen] = React.useState(false);
  const [title, setTitle] = React.useState("");
  // Mirrors the rendered anchor so the listeners can skip redundant work without
  // taking a render-time dependency on state. A ref rather than an effect-local
  // `let` so the release and the detached-node poll can clear it too — leaving
  // it set kept the node reachable by a second path, and made `handleEnter`
  // suppress every further hover inside a cell whose anchor had been dropped.
  const currentAnchorRef = React.useRef<HTMLElement | null>(null);
  // Pending anchor release. Held here so the listeners can cancel it the moment
  // a new anchor is adopted: an identity check alone is not enough, because
  // re-hovering the *same* element makes the stale timer's target and the fresh
  // anchor the same node, and it would drop the anchor that had just been set.
  const releaseTimerRef = React.useRef<number | undefined>(undefined);
  // Layout-free text signature of the anchor at the moment it was adopted, so
  // the poll can spot a rewrite without reading `innerText`.
  const signatureRef = React.useRef("");

  React.useEffect(() => {
    /** How long the pointer must rest before anything is measured. */
    const HOVER_DELAY_MS = 200;
    /** How far up from the hovered node to look for a truncating element. */
    const MAX_ANCESTOR_HOPS = 3;

    let timer: number | undefined;
    let frame: number | undefined;
    // The element a pending measurement is scheduled for, so typing into that
    // element can call it off before it opens a tooltip on top of the caret.
    let pendingTarget: HTMLElement | null = null;

    const cancel = () => {
      if (timer) window.clearTimeout(timer);
      if (frame) window.cancelAnimationFrame(frame);
      timer = undefined;
      frame = undefined;
      pendingTarget = null;
    };

    // Close only — the anchor is deliberately left in place so the exit
    // transition still has something to position against.
    const clear = () => {
      cancel();
      currentAnchorRef.current = null;
      setOpen(false);
    };

    const truncatesText = (el: HTMLElement) => {
      const style = window.getComputedStyle(el);
      // An engine that does not expose -webkit-line-clamp returns "" rather
      // than "none", so testing `!== "none"` alone made every element on the
      // page a candidate and left `isClipped` as the only filter.
      const clamp = style.getPropertyValue("-webkit-line-clamp");
      return (
        style.textOverflow === "ellipsis" || (clamp !== "" && clamp !== "none")
      );
    };

    const clippedTruncator = (el: HTMLElement) =>
      !isMaskedInput(el) && truncatesText(el) && isClipped(el) ? el : null;

    /**
     * The one element child, when the container has nothing of its own to say.
     *
     * The unit both directions below are built from: a chain of these is a chain
     * of boxes that are all "the same thing" as far as a hover is concerned,
     * because each holds nothing but the next.
     */
    const soleElementChild = (el: HTMLElement) => {
      if (el.children.length !== 1) return null;
      // `children` counts elements, so a text node beside the child has to be
      // asked about separately: "Prefix: <long name>" would otherwise reveal
      // only the name and read as a tooltip contradicting the cell under it.
      for (let i = 0; i < el.childNodes.length; i++) {
        const node = el.childNodes[i];
        if (node.nodeType === Node.TEXT_NODE && node.textContent?.trim()) {
          return null;
        }
      }
      return el.firstElementChild as HTMLElement;
    };

    /**
     * Down the sole-child chain to the first element that actually clips.
     *
     * A padded container hands its own box to the hover while the ellipsis lives
     * on something inside it, so without this every pixel of that padding finds
     * nothing. It has to follow the whole chain, not one step: this app's own
     * converted table cell is `td > span[data-cell-content] > the row's Box`,
     * where the span is width-constrained to 100% and never clips — only the Box
     * does. Stopping at the span found nothing, so the cell's 16px padding was
     * dead and, once the tooltip was open, crossing into it closed the tooltip
     * for good rather than merely blinking.
     */
    const soleTruncatingDescendant = (el: HTMLElement) => {
      let node = el;
      for (let depth = 0; depth < MAX_ANCESTOR_HOPS; depth++) {
        const child = soleElementChild(node);
        if (!child) return null;
        // `handleEnter` tested the hovered element and its ancestors for the
        // opt-out; `closest` never looks down, so a descendant it could not see
        // has to be tested here — otherwise a component that owns its own
        // tooltip gets this one stacked on top of it, which is the whole point
        // of OPT_OUT.
        if (child.closest(OPT_OUT)) return null;
        const hit = clippedTruncator(child);
        if (hit) return hit;
        node = child;
      }
      return null;
    };

    /**
     * The region the pointer may roam within without the tooltip going anywhere:
     * the anchor, widened out through every ancestor it is the sole content of.
     *
     * The exact mirror of the descent, and it has to be, or the two disagree
     * about where one "thing" ends. Hovering a padded container descends to the
     * label inside it, so hovering the label has to widen back out to the
     * container — otherwise crossing between them reads as leaving, `clear()`
     * runs, and the tooltip goes. Measured with a one-level version against the
     * three-level cell: opacity 1 → 0 on crossing into the cell padding, and it
     * never came back.
     */
    const scopeFor = (anchor: HTMLElement) => {
      let scope = anchor;
      for (let up = 0; up < MAX_ANCESTOR_HOPS; up++) {
        const parent = scope.parentElement;
        if (!parent || soleElementChild(parent) !== scope) break;
        scope = parent;
      }
      return scope;
    };

    /**
     * Derived on demand, never stored. A scope captured at measure time outlives
     * the invariant that justified it: a cell holding one clipped Box widens to
     * the whole cell, and a refetch that appends a second child — a status chip —
     * leaves the anchor untouched and connected, so nothing invalidates it. The
     * pointer moving onto that new chip would then be swallowed by the stale
     * scope and the tooltip would go on describing the first child. Recomputing
     * costs a few parent/child reads and no layout, and only while a tooltip is
     * actually open.
     */
    const currentScope = () => {
      const anchor = currentAnchorRef.current;
      return anchor ? scopeFor(anchor) : null;
    };

    const measure = (start: HTMLElement) => {
      // Every read below forces layout, so this runs in a frame of its own —
      // after the delay, and never inside the event handler itself.
      let el: HTMLElement | null = start;
      for (let hop = 0; el && hop <= MAX_ANCESTOR_HOPS; hop++) {
        // Stop rather than walk past it: an ancestor of a password field would
        // otherwise be offered up as the anchor on the next hop.
        if (isMaskedInput(el)) break;
        // Down one level, but only from the element actually hovered: deeper in
        // the walk the "container" is an ancestor the pointer never touched, and
        // its single child could be anything.
        const found =
          clippedTruncator(el) ??
          (hop === 0 ? soleTruncatingDescendant(el) : null);
        if (found) {
          const text = readText(found);
          // Nothing readable — fall through to the close below rather than
          // returning. Returning left whatever was already open still open, and
          // still anchored to the cell the pointer had since left.
          if (!text) break;
          // Adopting an anchor cancels any release still pending for the
          // previous one, including when it is this very element again.
          if (releaseTimerRef.current) {
            window.clearTimeout(releaseTimerRef.current);
            releaseTimerRef.current = undefined;
          }
          // Two different elements, and they are not interchangeable.
          //
          // The anchor is what the tooltip is *about*: `found`, the element that
          // truncates. It positions the popper and the open-tooltip poll
          // re-measures it, so pointing it at the padded container would have
          // the poll find an unclipped box and close immediately.
          //
          // The scope is the region the pointer may move within without the
          // tooltip going anywhere. Every containment test uses it. Derived from
          // the anchor rather than taken from `el`, so it comes out the same
          // whether the pointer arrived on the container or on the label — see
          // `scopeFor`.
          currentAnchorRef.current = found;
          signatureRef.current = signatureOf(found);
          setTitle(text);
          setAnchorEl(found);
          setOpen(true);
          return;
        }
        el = el.parentElement;
      }
      currentAnchorRef.current = null;
      setOpen(false);
    };

    const handleEnter = (event: Event) => {
      const target = event.target as HTMLElement | null;
      // Cheap guards first — none of this touches layout, so pointer travel
      // across the app costs a couple of property reads per element.
      if (!target || target.nodeType !== 1) return;
      // The popper is portaled to <body>, so a tooltip that flips under the
      // cursor would otherwise look like a brand new hover target and close
      // itself ~200ms after opening.
      if (target.closest(".MuiTooltip-popper")) return;
      // Owned by a component that supplies its own tooltip. Cancels rather than
      // bailing outright: `focusin` into an opted-out region would otherwise
      // leave a measurement scheduled for whatever was hovered before it, and
      // `focusout` does not fire when focus arrives from nothing.
      if (target.closest(OPT_OUT)) {
        cancel();
        return;
      }
      // Already showing this region's text: moving within a cell fires
      // mouseover again for each child, and re-measuring would be pure waste.
      // The scope, not the anchor — moving from a clipped label out into its
      // own container's padding is still the same option row.
      if (currentScope()?.contains(target)) return;
      cancel();
      pendingTarget = target;
      timer = window.setTimeout(() => {
        timer = undefined;
        frame = window.requestAnimationFrame(() => {
          frame = undefined;
          // Released before measuring, not left set: this is a long-lived
          // closure, so holding the last hovered node here pins its subtree.
          pendingTarget = null;
          measure(target);
        });
      }, HOVER_DELAY_MS);
    };

    // Scoped to the field being typed in. Closing on every `input` in the app
    // dismissed a tooltip the user was reading in a table because they happened
    // to type in a filter box, and since `clear()` also drops the anchor it
    // could not reopen until the pointer left the cell and came back.
    //
    // Both branches matter. Focusing a clipped field schedules a measurement,
    // so typing has to call that off as well — otherwise the tooltip opens a
    // moment later directly over the caret.
    const handleInput = (event: Event) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (pendingTarget && (pendingTarget === target || pendingTarget.contains(target))) {
        cancel();
      }
      const scope = currentScope();
      if (scope && (scope === target || scope.contains(target))) clear();
    };

    // Leaving the anchor has to close it. Without this the tooltip stays parked
    // over the table when the pointer exits upward to the browser chrome, since
    // no further mouseover, scroll or blur ever fires.
    const handleLeave = (event: MouseEvent) => {
      const next = event.relatedTarget as Node | null;
      if (next && currentScope()?.contains(next)) return;
      clear();
    };

    document.addEventListener("mouseover", handleEnter, true);
    document.addEventListener("mouseout", handleLeave, true);
    // Keyboard reveal for anything focusable — inputs and listbox options.
    document.addEventListener("focusin", handleEnter, true);
    document.addEventListener("focusout", clear, true);
    // A focused input keeps its tooltip while the user types, so the title
    // would sit over the field showing the value from before the keystroke.
    // Close instead of re-measuring: the text is right there being edited.
    document.addEventListener("input", handleInput, true);
    // The anchor moves out from under the pointer on any scroll.
    document.addEventListener("scroll", clear, true);
    window.addEventListener("pointerdown", clear, true);
    window.addEventListener("blur", clear);
    // Clipping is a function of width, so a resize can leave the title stale —
    // showing text that is no longer clipped — and the anchor moved. Closing
    // fails safe; the nav drawer's expand/collapse animates width too.
    window.addEventListener("resize", clear);

    return () => {
      cancel();
      document.removeEventListener("mouseover", handleEnter, true);
      document.removeEventListener("mouseout", handleLeave, true);
      document.removeEventListener("focusin", handleEnter, true);
      document.removeEventListener("focusout", clear, true);
      document.removeEventListener("input", handleInput, true);
      document.removeEventListener("scroll", clear, true);
      window.removeEventListener("pointerdown", clear, true);
      window.removeEventListener("blur", clear);
      window.removeEventListener("resize", clear);
    };
  }, []);

  // A row can be unmounted by a refetch or a delete while the tooltip is still
  // open. Nothing re-renders this component in that case, so the render-time
  // guard never re-evaluates and the popper stays anchored to a detached node,
  // which measures at the origin. Poll cheaply, and only while visible.
  //
  // The same poll also catches the anchor being *reused* rather than removed:
  // a refetch on a table whose rows have no id keys React by index, so the same
  // <td> survives with different text in it. isConnected stays true, and the
  // popper would go on showing the previous record's value.
  React.useEffect(() => {
    if (!open || !anchorEl) return;

    // Drop the listener's mirror too. Closing without it left `handleEnter`
    // believing the pointer was still inside this anchor, so hovering the
    // replacement row produced no tooltip at all.
    const closeAnchor = () => {
      if (currentAnchorRef.current === anchorEl) {
        currentAnchorRef.current = null;
      }
      setOpen(false);
    };

    const id = window.setInterval(() => {
      if (!anchorEl.isConnected) {
        closeAnchor();
        return;
      }
      // Both reads here are layout-free; only a confirmed rewrite pays for
      // `readText`, which is not.
      const signature = signatureOf(anchorEl);
      if (signature === signatureRef.current) return;
      signatureRef.current = signature;
      const text = readText(anchorEl);
      // Retitle in place rather than closing. A stationary pointer fires no
      // further mouseover, so closing here stranded the user: the tooltip
      // vanished mid-read and could not return until they moved the mouse.
      // Deliberately no re-measure from the anchor — `measure` walks *up*, and
      // from an anchor that no longer clips it would silently re-anchor to an
      // ancestor, whose `contains` guard then swallows hovers on every sibling.
      if (!text || !isClipped(anchorEl)) closeAnchor();
      else setTitle(text);
    }, 400);
    return () => window.clearInterval(id);
    // Not keyed on `title`: retitling must not restart the interval's phase.
  }, [open, anchorEl]);

  // `clear` keeps the anchor so the exit transition still has something to
  // position against, but nothing ever released it — the last hovered cell and
  // its whole subtree stayed reachable from state for the life of the app.
  // Release it once the transition has had time to finish; re-hovering flips
  // `open` back to true and the cleanup cancels the release.
  React.useEffect(() => {
    if (open || !anchorEl) return;
    const releasing = anchorEl;
    const id = window.setTimeout(() => {
      releaseTimerRef.current = undefined;
      setAnchorEl((current) => (current === releasing ? null : current));
      if (currentAnchorRef.current === releasing) {
        currentAnchorRef.current = null;
      }
    }, ANCHOR_RELEASE_MS);
    releaseTimerRef.current = id;
    return () => {
      window.clearTimeout(id);
      if (releaseTimerRef.current === id) releaseTimerRef.current = undefined;
    };
  }, [open, anchorEl]);

  // A row can re-render out from under a held anchor, leaving it detached; a
  // detached node measures at the origin, which is the same corner flash.
  const isAnchorUsable = Boolean(anchorEl && anchorEl.isConnected);

  // Kept mounted and toggled via `open` rather than returning null: moving
  // between two clipped cells then repositions the existing popper instead of
  // tearing one down and building another at pointer speed.
  return (
    <Tooltip
      open={open && isAnchorUsable}
      title={title}
      placement="top"
      // The popper must not take the pointer: it can flip under the cursor, and
      // an interactive popper then swallows hovers meant for the content below.
      disableInteractive
      PopperProps={isAnchorUsable ? { anchorEl: anchorEl as HTMLElement } : {}}
    >
      {/* Required anchor child; Popper positions against `anchorEl` instead.
          MUI puts its `aria-label` on this span rather than on the real anchor,
          so the wiring is inert — deliberately. CSS ellipsis does not truncate
          the accessibility tree: a screen reader already reads the cell's full
          text, so there is nothing here for it to recover. This tooltip is a
          visual affordance; don't "fix" the aria by pointing it at the span. */}
      <span />
    </Tooltip>
  );
}
